'use client';

import { cn } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { ROUTES, useIsMobile } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import type { LinkNavigationMenuItemProps } from '../../NavigationMenu/components/NavigationMenuItem/types';
import { NavigationMenu } from '../../NavigationMenu/NavigationMenu';
import { HiddenTrigger } from '../MyAccountSheet/MyAccountSheet';
import { SuspendedNotification } from '../SuspendedNotification/SuspendedNotification';
import { BalanceMenuItems } from './components/BalanceMenuItems/BalanceMenuItems';
import { FinancialStatementSheet } from './components/FinancialStatementSheet/FinancialStatementSheet';
import { LifetimeNetDepositsSheet } from './components/LifetimeNetDepositsSheet/LifetimeNetDepositsSheet';
import { TotalBalance } from './components/TotalBalance/TotalBalance';
import { useLatestTransactionSummary } from './hooks/useLatestTransactionSummary';
import { useWalletBalance } from './hooks/useWalletBalance';

type WalletProps = {
  namespace: string;
  className?: string;
};

function Wallet({ namespace, className }: WalletProps) {
  const t = useTranslations(namespace);
  const [showLifetimeNetDepositsSheet, setShowLifetimeNetDepositsSheet] = useState(false);
  const [showFinancialStateSheet, setShowFinancialStateSheet] = useState(false);
  const isMobile = useIsMobile();
  const { isFinancialStatementNavShown, isWalletApiLockedFundsEnabled, suspendedNotificationNamespaces } = getSiteConfig().MY_ACCOUNT_SETTINGS;

  const { accountSummary, isLoading: isWalletTransactionsLoading } = useLatestTransactionSummary();
  const { totalBalance, withdrawableBalance: walletWithdrawableBalance, lockedBalance, isLoading: isWalletBalanceLoading } = useWalletBalance();

  const { availableBalance, cashBalance, bonusBalance, cashLinkedToBonus } = accountSummary || {};

  const totalBalanceToShow = totalBalance ?? availableBalance;
  const cashBalanceToShow = walletWithdrawableBalance ?? cashBalance;
  const lockedFundsToShow = isWalletApiLockedFundsEnabled ? (lockedBalance ?? cashLinkedToBonus) : cashLinkedToBonus;
  const isBalanceLoading = isWalletTransactionsLoading || (isWalletBalanceLoading && !accountSummary);

  const transactionMenuItems: LinkNavigationMenuItemProps[] = [
    {
      label: t('transactions'),
      variant: 'link',
      href: ROUTES.MY_ACCOUNT.TRANSACTIONS,
      'data-track-name': 'View',
    },
  ];

  const paymentMethodsMenuItems: LinkNavigationMenuItemProps[] = [
    {
      label: t('payment_methods'),
      variant: 'link',
      href: ROUTES.MY_ACCOUNT.PAYMENT_METHODS,
      'data-track-name': 'View',
    },
  ];

  const lifetimeNetDepositsMenuItems: LinkNavigationMenuItemProps[] = [
    {
      label: t('lifetime_net_deposits'),
      variant: 'link',
      onClick: () => {
        setShowLifetimeNetDepositsSheet(true);
      },
      'data-testid': 'wallet-lifetime-net-deposits-menu-item',
      'data-track-name': 'View',
    },
  ];

  const financialStatementMenuItems: LinkNavigationMenuItemProps[] = [
    {
      label: isFinancialStatementNavShown ? t('financial_statement') : undefined,
      variant: 'link',
      onClick: () => {
        setShowFinancialStateSheet(true);
      },
      'data-testid': 'wallet-financial-statement-menu-item',
      'data-track-name': 'View',
    },
  ];

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <SuspendedNotification namespace={suspendedNotificationNamespaces.payments} variant="floating" />
      <TotalBalance namespace={namespace} availableBalance={totalBalanceToShow} className="pr-5" />
      <BalanceMenuItems
        cashBalance={cashBalanceToShow}
        bonusBalance={bonusBalance}
        lockedFunds={lockedFundsToShow}
        isWalletTransactionsLoading={isBalanceLoading}
        namespace={namespace}
        className={className}
      />
      <NavigationMenu variant="secondary" className={className} items={transactionMenuItems} data-track-section="Transactions" />
      <NavigationMenu variant="secondary" className={className} items={paymentMethodsMenuItems} data-track-section="Payment Methods" />
      <NavigationMenu variant="secondary" className={className} items={lifetimeNetDepositsMenuItems} data-track-section="Lifetime Deposits" />
      {isFinancialStatementNavShown ? (
        <>
          <NavigationMenu variant="secondary" className={className} items={financialStatementMenuItems} data-track-section="Financial Statement" />

          <FinancialStatementSheet
            contentClassName={isMobile ? 'h-[90vh]' : 'h-[100vh]'}
            open={showFinancialStateSheet}
            onOpenChange={setShowFinancialStateSheet}
            trigger={<HiddenTrigger label={t('financial_statement')} />}
          />
        </>
      ) : null}

      <LifetimeNetDepositsSheet
        contentClassName={isMobile ? 'h-[90vh]' : 'h-[100vh]'}
        open={showLifetimeNetDepositsSheet}
        onOpenChange={setShowLifetimeNetDepositsSheet}
        trigger={<HiddenTrigger label={t('lifetime_net_deposits')} />}
      />
    </div>
  );
}

export { Wallet };
